import { Record } from "immutable";
import { List, Map, OrderedMap } from "immutable";
import moment from "moment";
import {
    APPLICANT_TYPES,
    NATURE_OF_ASSOCIATION_TYPES,
    ADDRESSES_TYPES,
    RESIDENCE_TYPES,
    ACCOUNT_TYPES,
    GENDER_TYPES,
    CONSTITUTION_TYPES
} from "constants/enums";

import recordClassCreator from "./base-record";
import { BUSINESS_PRODUCT_TYPE } from "../constants";
import { getKeyByValue } from "../components/helper";

const COMPANY_MODEL_MAP = {
    id: null,
    addresses: new List([]),
    associates: new Map({}),
    bankAccounts: new List([]),
    bank_accounts: new Map({}),
    name: null,
    short_name: null,
    nature_of_business: [],
    type: null,
    date_of_gstin_return_filing: null,
    date_of_incorporation: null,
    place_of_incorporation: null,
    business_start_date: null,
    constitution: null,
    annual_turnover1_date: null,
    annual_turnover1: null,
    annual_turnover2_date: null,
    annual_turnover2: null,
    pan: null,
    gstin: null,
    cin: null,
    tin: null,
    registration_num: null,
    business_category: null,
    industry_type: null,
    num_of_employees: null,
    credit_rating: null,
    credit_rating_date: null,
    credit_rating_expiry_date: null,
    is_anchor: false,
    is_seller: false,
    is_buyer: false,
    current_status: null,
    company_cibil_score: null,
    channel_products: [],
    company_cibil_remarks: null,
    is_main_borrower: null,
    shareholding: null,
    is_active: true,
    created_by: null,
    modified_by: null,
    create_date: null,
    modified_date: null,
    primary_relationship_manager: null,
    date_of_gstin_return_filling: null,
    type_of_scheme: null
};

export class CompanyRecord extends recordClassCreator(COMPANY_MODEL_MAP) {
    get constitution_name() {
        return CONSTITUTION_TYPES.findKey(_ => _ === this.constitution);
    }

    get operating_address() {
        return this.addresses.find(
            _ => _.address_type === ADDRESSES_TYPES.get("Office")
        );
    }

    get registered_address() {
        return this.addresses.find(
            _ => _.address_type === ADDRESSES_TYPES.get("Registered")
        );
    }

    get cibilReportTable() {
        return this.associates && this.associates.map(_ => _.CIBIL_Details);
    }

    get associatesTableData() {
        return this.associates && this.associates.size
            ? this.associates.map(_ =>
                _.toMap().set(
                    "address",
                    this.addresses.find(__ => __.associate === _.id)
                )
            )
            : new Map({});
    }

    getCompanyProfile() {
        return {
            name: this.name,
            nature_of_business: this.nature_of_business,
            date_of_incorporation: !this.date_of_incorporation.format("YYYY-MM-DD").includes("Invalid") ? this.date_of_incorporation.format("YYYY-MM-DD") : null,
            business_start_date: !this.business_start_date.format("YYYY-MM-DD").includes("Invalid") ? this.business_start_date.format("YYYY-MM-DD") : null,
            constitution: this.constitution,
            pan: this.pan,
            gstin: this.gstin,
            cin: this.cin,
            registration_num: this.registration_num,
            business_category: this.business_category,
            num_of_employees: this.num_of_employees,
            company_cibil_score: this.company_cibil_score,
            company_cibil_remarks: this.company_cibil_remarks,
            is_active: this.is_active
        };
    }

    getAssociates() {
        return (
            this.associates &&
            this.associates
                .filter(_ => _.type === 1)
                .map(_ => {
                    return {
                        id: _.id,
                        name: _.first_name + _.last_name,
                        gender: _.gender === 1 ? "Male" : "Female",
                        dob: _.dob
                            ? moment(this.dob).format("DD-MM-YYY")
                            : null,
                        phone: _.phone,
                        email: _.email,
                        nature_of_association: _.nature_of_association,
                        pan: _.pan,
                        present_address: null
                    };
                })
        );
    }

    getGuarantors() {
        return (
            this.associates &&
            this.associates
                .filter(_ => _.type === 2)
                .map(_ => {
                    return {
                        id: _.id,
                        name: _.first_name + this.last_name,
                        gender: _.gender === 1 ? "Male" : "Female",
                        dob: _.dob ? moment(_.dob).format("DD-MM-YYY") : null,
                        phone: _.phone,
                        email: _.email,
                        nature_of_association: _.nature_of_association,
                        pan: _.pan,
                        present_address: null
                    };
                })
        );
    }
    getBankAccounts() {
        return (
            this.bank_accounts &&
            this.bank_accounts.map(_ => ({
                account_number: _.account_number,
                account_type: _.account_type,
                account_holder_name: _.account_holder_name,
                ifsc_code: _.bank_branch.ifsc_code
            }))
        );
    }

    get isKeyBankAccount() {
        return (
            this.bank_accounts && this.bank_accounts.find(_ => _.is_key_account)
        );
    }
    get hasOperatingAddress() {
        return this.addresses && this.addresses.find(_ => _.address_type === 2);
    }

    get hasRegisteredAddress() {
        return this.addresses && this.addresses.find(_ => _.address_type === 1);
    }

    get hasMainBorrower() {
        return this.associates && this.associates.find(_ => _.is_main_borrower);
    }

    get hasPrimaryRelationshipManager() {
        return this.primary_relationship_manager && this.primary_relationship_manager.id;
    }

    get doesAllApplicantsHaveCibil() {
        return (
            this.associates &&
            this.associates.reduce(
                (agg, cur) => (agg && cur.cibil_score > -1 ? true : false),
                true
            )
        );
    }

    get doesAllExistingLimits() {
        return (
            this.bank_accounts &&
            this.bank_accounts
                .filter(_ => [3, 4].includes(_.account_type))
                .reduce(
                    (agg, cur) =>
                        agg && cur.account_limit > -1 ? true : false,
                    true
                )
        );
    }

    get existing_limits() {
        return this.bank_accounts
            ? this.bank_accounts
                .filter(_ => [3, 4].includes(_.account_type))
                .reduce((agg, _) => agg + (+_.account_limit || 0), 0)
            : 0;
    }

    get isGuarrantorRequired() {
        return this.associates
            .filter(_ => _.type === 1 && _.is_main_borrower)
            .map(_ => _.dob)
            .reduce((agg, _) => {
                const age = moment().diff(_, "years", true);
                if (age <= 25 || age > 65) {
                    return true;
                }
                return false;
            }, false);
    }

    get high_cibil() {
        return this.associates
            .map(_ => _.max_cibil_score)
            .toList()
            .sort()
            .last();
    }

    get main_borrower_age() {
        return (
            this.associates.find(_ => _.is_main_borrower) || {
                dob_and_age: "--"
            }
        ).dob_and_age;
    }

    get applicants_list() {
        return this.associates.map(_ => {
            return new OrderedMap({})
                .set("Name", _.name)
                .set("Pan", _.pan)
                .set(
                    "Association",
                    NATURE_OF_ASSOCIATION_TYPES.findKey(
                        __ => __ == _.nature_of_association
                    )
                )
                .set("Type", APPLICANT_TYPES.findKey(__ => __ == _.type))
                .set("Main Borrower", _.is_main_borrower ? "Yes" : "No")
                .set("Gender", GENDER_TYPES.findKey(__ => __ == _.gender))
                .set("Date of Birth", _.dob && _.dob.format("MM-DD-YYYY"))
                .set("Phone", _.phone)
                .set("Email", _.email)
                .map((value, key) => ({ value, key }))
                .toList();
        });
    }

    get property_ownership() {
        const office = this.addresses
            .filter(_ => _.associate === null)
            .find(_ => _.ownership_type === 1);
        const residence = this.addresses
            .filter(_ => _.associate !== null)
            .find(_ => _.ownership_type === 1);
        if (office && residence) {
            return "Office and Residence";
        }
        if (office) {
            return "Office";
        }
        if (residence) {
            return "Residence";
        }
        return "None";
    }
}

export class BankAccountRecord extends Record({
    id: null,
    account_holder_name: null,
    bank_branch: null,
    company: null,
    associate: null,
    account_type: null,
    account_number: null,
    account_limit: null,
    status: null,
    bank_cheques: new List([]),
    bank_nach: new List([]),
    is_active: null,
    is_key_account: false,
    created_by: null,
    modified_by: null,
    create_date: null,
    modified_date: null,
    code: null,
    is_applicable: null
}) {
    get description_list() {
        const list = new OrderedMap({});

        return list.withMutations(_ =>
            _.set(
                "A/C Type",
                ACCOUNT_TYPES.findKey(value => value === this.account_type)
            )
                .set("A/C Holder Name", this.account_holder_name)
                .set("A/C Number", this.account_number)
                .set("A/C Limit", this.account_limit)
                .set("IFSC", this.ifsc_code)
                .set("Bank", this.bank_name)
                .set("Branch", this.branch_name)
        );
    }

    get name() {
        return `${this.bank_branch && this.bank_branch.bank_name}-${this.bank_branch && this.bank_branch.name} (${this.bank_branch && this.bank_branch.ifsc_code})`;
    }
    get ifsc_code() {
        return this.bank_branch && this.bank_branch.ifsc_code;
    }

    get bank_name() {
        return this.bank_branch && this.bank_branch.bank_name;
    }

    get branch_name() {
        return this.bank_branch && this.bank_branch.name;
    }

    get bank_branch_id() {
        return this.bank_branch && this.bank_branch.id;
    }

    get form() {
        return this.toSeq()
            .filter((_, key) =>
                [
                    "account_holder_name",
                    "account_type",
                    "account_number",
                    "associate",
                    "bank_branch"
                ].includes(key)
            )
            .set("ifsc_code", this.bank_branch)
            .toMap();
    }

    get formValues() {
        return this.toSeq()
            .filter((_, key) =>
                [
                    "id",
                    "company",
                    "associate",
                    "account_type",
                    "account_number",
                    "account_holder_name",
                    "account_limit",
                    "status",
                    "is_key_account",
                    "is_applicable"
                ].includes(key)
            )
            .toMap()
            .set("associate", this.associate === "company" ? null : this.associate)
            .set("bank_branch", this.bank_branch_id)
            .toJS();
    }
}

export class AddressRecord extends Record({
    id: null,
    company: null,
    associate: null,
    address_type: null,
    ownership_type: null,
    residing_since: null,
    address_line_1: null,
    address_line_2: null,
    city: null,
    district: null,
    state: null,
    country: null,
    pincode: null,
    is_pv_done: false,
    is_communication_address: null,
    is_active: null
}) {
    get formValues() {
        return this.toSeq()
            .toMap()
            .filter((_, key) =>
                [
                    "id",
                    "company",
                    "associate",
                    "address_type",
                    "ownership_type",
                    "address_line_1",
                    "address_line_2",
                    "pincode",
                    "is_pv_done",
                    "is_communication_address",
                    "is_active"
                ].includes(key)
            )
            .set(
                "residing_since",
                this.residing_since
                    ? this.residing_since.format("YYYY-MM-DD")
                    : null
            )
            .set("city", this.city_id)
            .set("state", this.state_id)
            .set("country", 1)
            .set("district", this.district_id)
            .toJS();
    }

    get formatted_string() {
        return `${this.address_line_1},
        ${this.address_line_2 ? this.address_line_2 + "," : ""}
        ${this.city_name ? this.city_name + "," : ""}${this.district_name ? this.district_name + "," : ""}${this.state_name ? this.state_name + "," : ""}
        ${this.country_name}-${this.pincode} (${this.ownership})`;
    }

    get city_id() {
        if (this.city && this.city.id) {
            return this.city.id;
        } else if (this.city && this.city.name && !this.city.id) {
            let district = this.district && this.district.id;
            if (!district) {
                district = this.district.name;
            }
            return {
                name: this.city.name,
                district: district,
                state: this.state_id
            };
        }
        return this.city ? this.city.id : null;
    }

    get state_id() {
        return this.state ? this.state.id : null;
    }
    get district_id() {
        return this.district ? this.district.id : null;
    }

    get city_name() {
        return this.city && this.city.name;
    }
    get state_name() {
        return this.state && this.state.name;
    }
    get district_name() {
        return this.district && this.district.name;
    }

    get country_name() {
        return this.country && this.country.name;
    }

    get ownership() {
        return (
            this.ownership_type &&
            RESIDENCE_TYPES.flip().find(
                (value, key) => key === this.ownership_type
            )
        );
    }
    get residing_date() {
        return this.residing_since && this.residing_since.format("DD/MM/YYYY");
    }
}

export class AssociateRecord extends Record({
    id: null,
    company: null,
    type: null,
    nature_of_association: null,
    first_name: null,
    middle_name: null,
    last_name: null,
    email: null,
    email_2: null,
    phone: null,
    phone_office: null,
    phone_residence: null,
    pan: null,
    aadhar: null,
    din: null,
    passport: null,
    passport_issue_date: null,
    passport_expiry_date: null,
    father_name: null,
    mother_name: null,
    spouse_name: null,
    dob: null,
    title: null,
    gender: null,
    category: null,
    nationality: null,
    education: null,
    marital_status: null,
    gross_annual_income: null,
    vintage_current_business_months: null,
    total_experience_months: null,
    profession: null,
    no_of_dependent_adult: null,
    no_of_dependent_children: null,
    is_key_contact: false,
    create_date: null,
    modified_date: null,
    created_by: null,
    modified_by: null,
    cibil_remarks: null,
    cibil_score: null,
    bureau: null,
    personal_cibil_score: null,
    cibil_os: null,
    cibil_report: null,
    cibil_supported_report: new List([]),
    is_main_borrower: false,
    shareholding: null,
    annual_income: null,
    is_active: null,
    cibil_score_range: null,
    crif_score: null,
    is_new_to_credit: null,
    case_level_data: null,
    comments: null,
    conditions: null,
    descriptions: null,
    name_pan: null,
    dob_pan: null,
    name_aadhar: null,
    dob_aadhar: null,
    name_gst: null,
    name_pdeed: null,
    is_vernacular_required: true,
    residence_address : null,
    permanent_address : null,
    key_source: null,
    aadhar_pan_link_document: null
}) {
    get key() {
        return this.id;
    }
    toDisplayForm(residence_address, permanent_address) {
        return new Map({})
            .set("type", APPLICANT_TYPES.findKey(_ => _ === this.type))
            .set("name", this.name)
            .set("first_name", this.first_name)
            .set("middle_name", this.middle_name)
            .set("last_name", this.last_name)
            .set("aadhar", this.aadhar)
            .set("marital_status", this.marital_status)
            .set("profession", this.profession)
            .set("father_name", this.father_name)
            .set("spouse_name", this.spouse_name)
            .set("mother_name", this.mother_name)
            .set("gender", GENDER_TYPES.findKey(_ => _ === this.gender) || "--")
            .set(
                "dob",
                this.dob && this.dob.format("DD-MM-YYYY") === "Invalid Date"
                    ? "--"
                    : this.dob && this.dob.format("DD-MM-YYYY")
            )
            .set("phone", this.phone)
            .set("phone_office", this.phone_office)
            .set("phone_residence", this.phone_residence)
            .set("email", this.email)
            .set(
                "nature_of_association",
                NATURE_OF_ASSOCIATION_TYPES.findKey(
                    _ => _ === this.nature_of_association
                )
            )
            .set("pan", this.pan)
            .set("is_main_borrower", this.is_main_borrower ? "Yes" : "No")
            .set("residence_address", residence_address)
            .set("permanent_address", permanent_address)
            .set("annual_income", this.annual_income)
            .set("is_active", this.is_active)
            .set(
                "shareholding",
                this.shareholding >= 0 ? this.shareholding : "--"
            )
            .set("name_pan", this.name_pan)
            .set("dob_pan",
                "dob_pan",
                this.dob_pan && this.dob_pan.format("DD-MM-YYYY") === "Invalid Date"
                    ? "--"
                    : this.dob_pan && this.dob_pan.format("DD-MM-YYYY")
            )
            .set("name_aadhar", this.name_aadhar)
            .set("dob_aadhar", this.dob_aadhar && this.dob_aadhar.format("DD-MM-YYYY") === "Invalid Date"
                ? "--"
                : this.dob_aadhar && this.dob_aadhar.format("DD-MM-YYYY"))
            .set("name_pdeed", this.name_pdeed)
            .set("aadhar_pan_link_document", this.aadhar_pan_link_document);
    }

    get formValues() {
        return this.toSeq()
            .toMap()
            .filter((_, key) =>
                [
                    "id",
                    "type",
                    "cibil_score",
                    "aadhar",
                    "cibil_remarks",
                    "cibil_supported_report",
                    "personal_cibil_score",
                    "cibil_os",
                    "company",
                    "first_name",
                    "middle_name",
                    "last_name",
                    "father_name",
                    "spouse_name",
                    "mother_name",
                    "pan",
                    "phone",
                    "phone_office",
                    "phone_residence",
                    "email",
                    "dob",
                    "dob_aadhar",
                    "gender",
                    "nature_of_association",
                    "is_main_borrower",
                    "shareholding",
                    "annual_income",
                    "marital_status",
                    "profession",
                    "is_active",
                    "bureau",
                    "name_pan",
                    "dob_pan",
                    "name_aadhar",
                    "dob_aadhar",
                    "name_gst",
                    "name_pdeed",
                    "is_vernacular_required",
                    "aadhar_pan_link_document"
                ].includes(key)
            )
            .set(
                "dob",
                this.dob && this.dob._isMomentObject && !isNaN(this.dob.date()) ?
                    this.dob.format("YYYY-MM-DD") :
                    this.dob && moment(this.dob) && !isNaN(moment(this.dob).date()) ?
                        moment(this.dob).format("YYYY-MM-DD") : null
            )
            .set(
                "dob_pan",
                this.dob_pan && this.dob_pan._isMomentObject && !isNaN(this.dob_pan.date()) ?
                    this.dob_pan.format("YYYY-MM-DD") :
                    this.dob_pan && moment(this.dob_pan) && !isNaN(moment(this.dob_pan).date()) ?
                        moment(this.dob_pan).format("YYYY-MM-DD") : null
            )
            .set(
                "dob_aadhar",
                this.dob_aadhar && this.dob_aadhar._isMomentObject && !isNaN(this.dob_aadhar.date()) ?
                    this.dob_aadhar.format("YYYY-MM-DD") :
                    this.dob_aadhar && moment(this.dob_aadhar) && !isNaN(moment(this.dob_aadhar).date()) ?
                        moment(this.dob_aadhar).format("YYYY-MM-DD") : null
            )
            .set(
                "cibil_report",
                this.cibil_report ? this.cibil_report.id : null
            )
            .set(
                "cibil_supported_report",
                this.cibil_supported_report &&
                    (this.cibil_supported_report.size ||
                        this.cibil_supported_report.length) > 0
                    ? this.cibil_supported_report.map(_ => _.id)
                    : []
            )
            .toJS();
    }

    get name() {
        return this.middle_name
            ? this.last_name
                ? `${this.first_name} ${this.middle_name} ${this.last_name}`
                : `${this.first_name} ${this.middle_name}`
            : this.last_name
                ? `${this.first_name} ${this.last_name} `
                : `${this.first_name}`;
    }

    get nameValue() {
        if (!this.first_name && !this.last_name) {
            return null;
        }
        return this.toSeq()
            .toMap()
            .filter((_, key) =>
                ["first_name", "middle_name", "last_name"].includes(key)
            )
            .toJS();
    }

    get option() {
        return {
            label: this.name,
            value: this.id
        };
    }

    get CIBIL_Details() {
        return {
            id: this.id,
            name: this.name,
            cibil_score: this.cibil_score,
            cibil_remarks: this.cibil_remarks,
            cibile_report: this.cibil_report,
            cibil_supported_report: this.cibil_supported_report
        };
    }

    get dob_and_age() {
        return this.dob ? `${moment().diff(this.dob, "years")} years` : null;
    }

    getName() {
        return this.middle_name
            ? this.last_name
                ? `${this.first_name} ${this.middle_name} ${this.last_name}`
                : `${this.first_name} ${this.middle_name}`
            : this.last_name
                ? `${this.first_name} ${this.last_name} `
                : `${this.first_name}`;
    }
    getProfileItem() {
        return {
            key: this.id,
            id: this.id,
            name: this.getName(),
            type: APPLICANT_TYPES.findKey(_ => _ === this.type),
            primary_email: this.primary_email,
            nature_of_association: NATURE_OF_ASSOCIATION_TYPES.findKey(
                _ => _ === this.nature_of_association
            ),
            phone: this.phone
        };
    }

    get max_cibil_score() {
        if (this.personal_cibil_score && this.personal_cibil_score > this.cibil_score) {
            return this.personal_cibil_score;
        }
        return this.cibil_score;
    }

    get parserFieldValues() {
        return {
            first_name: this.first_name,
            last_name: this.last_name,
            father_name: this.father_name,
            dob: this.dob ? moment(this.dob) : null,
            pan: this.pan,
            aadhar: this.aadhar,
            gender: this.gender ? this.gender : null,
            phone: this.phone,
            is_active: this.is_active,
            name_pan: this.name_pan,
            mother_name: this.mother_name,
            name_aadhar: this.name_aadhar,
            dob_pan: this.dob_pan? moment(this.dob_pan):null,
            dob_aadhar: this.dob_aadhar? moment(this.dob_aadhar):null,
            name_pdeed: this.name_pdeed,
            is_vernacular_required: this.is_vernacular_required,
        };
    }
}

export class CompanyBankingRecord extends Record({
    case_id: null,
    case_bank_account_id: null,
    txn_month_year: null,
    no_of_txns: null,
    average_balance: null,
    min_balance: null,
    max_balance: null,
    total_cheque: null,
    total_cheque_bounce: null,
    total_credit_amount: null,
    total_debit_amount: null,
    top_5_creditors_amount: null,
    top_5_debitors_amount: null
}) { }

export class CompanyFinancialRecord extends Record({
    case_id: null,
    financial_year: null,
    current_assets: null,
    current_liabilities: null,
    current_ratio: null,
    PBT: null,
    EBDITA: null,
    interest: null,
    interest_coverage: null,
    debt: null,
    equity: null,
    debt_equity_ratio: null,
    PAT: null,
    ROCE: null,
    average_stock: null,
    COGS: null,
    stock_turnover_in_months: null,
    average_debtors: null,
    sales: null,
    debtors_turnover_in_months: null,
    average_creditors: null,
    purchases: null,
    creditors_turnover_in_months: null
}) { }

export class MpaApprovalDetailRecord extends Record({
    action_detail_id: null,
    adhoc_rate_perc: null,
    anchor_fees_approved_by: null,
    approval_date: null,
    association_type: null,
    blended_roi_target_perc: null,
    ccm_meeting_date: null,
    charges_at: null,
    company: null,
    current_status: null,
    days_of_guarantee_invocation: null,
    days_of_stale_invoice: null,
    db_risk_controls_approved_by: null,
    deviation_matrix_approved_by: null,
    disbursal_controls_approved_by: null,
    eml_file: null,
    fee_amount: null,
    fee_basis: null,
    fee_perc: null,
    holdback_amount: null,
    holdback_perc: null,
    security_perc: null,
    security_amount: null,
    id: null,
    ps_kit : null,
    deviation_matrix_status: null,
    db_risk_controls_status: null,
    scheme_controls_status: null,
    onboarding_controls_status: null,
    disbursal_controls_status: null,
    anchor_fees_status: null
}) {}

export class OnboardingCasesRecord extends Record({
    id: null,
    last_action_type: null,
    mpa_approval_id: null,
    state: null,
    status: null,
    current_action_task: null,
    company : null,
    mpa_approval: new MpaApprovalDetailRecord(),
    product_type: null,
    proposed_program_size_in_cr: null,
}) { 
    getStageID(){
        return this.current_action_task.this_stage && this.current_action_task.this_stage.id;
    }
    getStageName(){
        return this.current_action_task && this.current_action_task.this_stage && this.current_action_task.this_stage.stage_name;
    }

    getProductName(){
        return getKeyByValue(BUSINESS_PRODUCT_TYPE ,this.mpa_approval.product_type);
    }

    getProductNameDBLIST(){
        return getKeyByValue(BUSINESS_PRODUCT_TYPE ,this.product_type);
    }

    
    getStageIDV2(){
        return this.mpa_approval.action_detail && this.mpa_approval.action_detail.current_action_task.this_stage && this.mpa_approval.action_detail.current_action_task.this_stage.id;
    }

    getStageNameAllMpa(){
        return this.mpa_approval.action_detail && this.mpa_approval.action_detail?.last_action_type || this.mpa_approval.action_detail && this.mpa_approval.action_detail.current_action_task.this_stage && this.mpa_approval.action_detail.current_action_task.this_stage.stage_name;
    }
    getAssignedUser(){
        return this.current_action_task && this.current_action_task.this_user;
    }

    getPrimaryIndustry(){
        return this.company && this.company.industry && this.company.industry.name
    }

    get_action_task_id(){
        return this.current_action_task && this.current_action_task.id
    }
}
